import React, { createContext, useContext, useState, useEffect } from 'react';

const NavigationContext = createContext();

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return context;
};

export const NavigationProvider = ({ children }) => {
  const [previousPage, setPreviousPage] = useState(null);
  const [pageState, setPageState] = useState({});

  // Save current page state before navigation
  const savePage = (path, state = {}) => {
    const pageInfo = {
      path,
      state,
      timestamp: Date.now()
    };
    
    // Save to localStorage for persistence across browser sessions
    // NOTE: We only save to localStorage, not React state, to avoid re-renders during navigation
    try {
      localStorage.setItem('nav_previous_page', JSON.stringify(pageInfo));
    } catch (e) {
      console.warn('Failed to save navigation state to localStorage:', e);
    }
    
    // Update pageState for same-session state retrieval (without previousPage to avoid re-renders)
    setPageState(prev => ({
      ...prev,
      [path]: state
    }));
  };

  // Get saved page state
  const getPageState = (path) => {
    return pageState[path] || {};
  };

  // Restore previous page from localStorage on app load
  useEffect(() => {
    try {
      const saved = localStorage.getItem('nav_previous_page');
      if (saved) {
        const pageInfo = JSON.parse(saved);
        // Only restore if it's recent (within 1 hour)
        if (Date.now() - pageInfo.timestamp < 3600000) {
          setPreviousPage(pageInfo);
          setPageState(prev => ({
            ...prev,
            [pageInfo.path]: pageInfo.state
          }));
        } else {
          // Clean up old navigation state
          localStorage.removeItem('nav_previous_page');
        }
      }
    } catch (e) {
      console.warn('Failed to restore navigation state from localStorage:', e);
    }
  }, []);

  // Get the path to go back to
  const getBackPath = () => {
    return previousPage?.path || '/';
  };

  // Clear navigation state
  const clearNavigation = () => {
    setPreviousPage(null);
    setPageState({});
    localStorage.removeItem('nav_previous_page');
  };

  const value = {
    savePage,
    getPageState,
    getBackPath,
    clearNavigation,
    previousPage
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};